import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  Alert,
} from "@mui/material";
import EmptyRows from "../../../../components/reusableComponent/EmptyRows";
import TableModal from "../../../../components/reusableComponent/TableModal";
import { useGetEmailFirstResponseClickToExpandQuery } from "../../../../Services/dashboard/dashboard";

const MAX_SELECTED_DISPOSITIONS = 5;
const DISPOSITION_LIMIT_ERROR = `You can select up to ${MAX_SELECTED_DISPOSITIONS} dispositions only. Please remove one to select another.`;

const MIN_CHART_HEIGHT = 270;

const DISPOSITION_MENU_PROPS = {
  PaperProps: { 
    style: {
      maxHeight: 280,
      overflowY: "auto" as const,
    },
  },
};

interface IPROPS {
  emailDashboardAnalyticsData: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
}

const DispositionCards: React.FC<IPROPS> = ({
  emailDashboardAnalyticsData,
  startDate,
  endDate,
  teamId,
}) => {
  const navigate = useNavigate();
  const [selectedCount, setSelectedCount] = useState<number>(5);
  const [selectedDispositions, setSelectedDispositions] = useState<string[]>([]);
  const [dispositionSelectError, setDispositionSelectError] = useState("");
  const [chartData, setChartData] = useState<any[]>([]);
  
  // Get default top 5 dispositions for pre-selection
  const defaultTopDispositions = useMemo(() => {
    const allDispositions = emailDashboardAnalyticsData?.all_dispositions || emailDashboardAnalyticsData?.top_five_dispositions || [];
    return allDispositions.slice(0, selectedCount).map((item: any) => item.disposition_name);
  }, [emailDashboardAnalyticsData, selectedCount]);
  
  const totalDispositionCount = chartData.reduce((total, item) => total + item.value, 0);
  const emailDashboardDisposition = "disposition";

  // Function to generate random color based on index
  const getRandomColor = (index: number) => {
    const hue = (index * 137.508) % 360; // Golden angle approximation for good distribution
    const saturation = 70 + (index % 20); // Vary saturation slightly
    const lightness = 45 + (index % 15); // Vary lightness slightly
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const [allZero, setAllZero] = useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedDisposition, setSelectedDisposition] = useState<{
    disposition_id: number;
    disposition_name: string;
  } | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);
  const PieChartContainer = PieChart as any;
  const PieContainer = Pie as any;
  const CellContainer = Cell as any;
  const ResponsiveChartContainer = ResponsiveContainer as any;
  const LegendContainer = Legend as any;
  const TooltipContainer = Tooltip as any;

  const chartAreaHeight = useMemo(() => {
    const count = chartData.length;
    if (count === 0) return MIN_CHART_HEIGHT;
    const legendHeight = count * 44 + (count - 1) * 6;
    return Math.max(MIN_CHART_HEIGHT, legendHeight);
  }, [chartData.length]);

  const clickToExpandParams =
    tableModalOpen && selectedDisposition && teamId && startDate && endDate
      ? {
          graph_name: "top_five_dispositions",
          value: String(selectedDisposition.disposition_id),
          start_date: startDate,
          end_date: endDate,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetEmailFirstResponseClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams }
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 10;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedDisposition?.disposition_id]);

  // Initialize selected dispositions with default top 5 when data changes
  useEffect(() => {
    if (defaultTopDispositions.length > 0 && selectedDispositions.length === 0) {
      setSelectedDispositions(defaultTopDispositions);
    }
  }, [defaultTopDispositions]);

  useEffect(() => {
    // Get all available dispositions (either from all_dispositions or fallback to top_five_dispositions)
    const allDispositions = emailDashboardAnalyticsData?.all_dispositions || emailDashboardAnalyticsData?.top_five_dispositions || [];
    
    // If no dispositions available or all are deselected, show empty state
    if (!allDispositions.length || selectedDispositions.length === 0) {
      setAllZero(true);
      setChartData([]);
      return;
    }

    // Show selected dispositions
    const dispositionsToShow = allDispositions.filter((item: any) => 
      selectedDispositions.includes(item.disposition_name)
    );

    const normalized = dispositionsToShow.map((item: any, index: number) => ({
      name: item.disposition_name,
      value: Number(item.count ?? 0),
      fill: getRandomColor(index),
      disposition_id: item.disposition_id,
    }));

    setAllZero(normalized.every(d => d.value === 0));
    setActiveIndex(undefined);
    setChartData(normalized);
  }, [emailDashboardAnalyticsData, selectedCount, selectedDispositions]);

  const handleDispositionRowClick = (dispositionInfo: any) => {
    setSelectedDisposition({
      disposition_id: dispositionInfo.disposition_id,
      disposition_name: dispositionInfo.disposition_name ?? "Disposition",
    });
    setTableModalOpen(true);
    setClickToExpandPage(1);
  };

  const dispositionOptions =
    emailDashboardAnalyticsData?.all_dispositions ||
    emailDashboardAnalyticsData?.top_five_dispositions ||
    [];

  const showDispositionLimitError = () => {
    setDispositionSelectError(DISPOSITION_LIMIT_ERROR);
  };

  const handleDispositionChange = (e: SelectChangeEvent<string[]>) => {
    const value =
      typeof e.target.value === "string"
        ? e.target.value.split(",")
        : e.target.value;

    if (value.length > MAX_SELECTED_DISPOSITIONS) {
      showDispositionLimitError();
      return;
    }

    setDispositionSelectError("");
    setSelectedDispositions(value);
  };

  const handleSegmentClick = (index: number) => {
    if (!teamId || !startDate || !endDate) return;
    const disposition = chartData[index];
    if (disposition) {
      setSelectedDisposition({
        disposition_id: disposition.disposition_id,
        disposition_name: disposition.name,
      });
      setTableModalOpen(true);
      setClickToExpandPage(1);
    }
  };

  const CustomLegend = () => {
    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {chartData.map((item, index) => {
          const percentage = total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
          const isActive = activeIndex === index;
          
          return (
            <div
              key={item.name}
              role="button"
              onClick={() => handleSegmentClick(index)}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(undefined)}
              style={{
                padding: "8px 12px",
                borderRadius: 6,
                cursor: teamId && startDate && endDate ? "pointer" : "default",
                background: isActive ? `${item.fill}10` : "#f8fafc",
                border: `1.5px solid ${isActive ? item.fill : 'transparent'}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "all 0.2s ease",
                transform: isActive ? "scale(1.02)" : "scale(1)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: item.fill,
                    boxShadow: isActive ? `0 0 8px ${item.fill}80` : 'none',
                  }}
                />
                <span style={{ 
                  color: "#374151", 
                  fontWeight: isActive ? "600" : "500",
                  fontSize: "13px",
                  marginRight: "5px"
                }}>
                  {item.name}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ 
                  color: "#9ca3af", 
                  fontSize: "12px",
                  fontWeight: "500"
                }}>
                  {percentage}%
                </span>
                <strong style={{ 
                  color: isActive ? item.fill : "#1f2937", 
                  fontSize: "14px",
                  minWidth: "30px",
                  textAlign: "right"
                }}>
                  {item.value}
                </strong>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0];
    const total = payload[0].payload.value;

    return (
      <div
        style={{
          background: "white",
          padding: "8px 12px",
          borderRadius: 6,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          border: `2px solid ${data.payload.fill}`,
        }}
      >
        <div style={{ 
          color: data.payload.fill, 
          fontWeight: "600",
          fontSize: "13px",
          marginBottom: 2
        }}>
          {data.name}
        </div>
        <div style={{ color: "#6b7280", fontSize: "12px" }}>
          {total} dispositions
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="card card-flush h-lg-100">
        <div className="px-5 pt-5">
          <div className="d-flex justify-content-between align-items-start">
            <h3 className="card-title mb-0">
              <span className="fs-2 fw-bold text-gray-800">
                Top {selectedDispositions.length === 0 ? '5' : selectedDispositions.length} dispositions : {totalDispositionCount}
              </span>
            </h3>
            <FormControl
              sx={{ minWidth: 180, maxWidth: 220 }}
              error={!!dispositionSelectError}
            >
              <InputLabel id="disposition-multi-select-label">Select Dispositions</InputLabel>
              <Select
                labelId="disposition-multi-select-label"
                id="disposition-multi-select"
                multiple
                value={selectedDispositions}
                onChange={handleDispositionChange}
                MenuProps={DISPOSITION_MENU_PROPS}
                input={<OutlinedInput label="Select Dispositions" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return `Top ${selectedCount}`;
                  }
                  return selected.join(", ");
                }}
              >
                {dispositionOptions.map((disposition: any) => {
                  const isSelected =
                    selectedDispositions.indexOf(disposition.disposition_name) > -1;
                  const atMax =
                    selectedDispositions.length >= MAX_SELECTED_DISPOSITIONS;

                  return (
                    <MenuItem
                      key={disposition.disposition_name}
                      value={disposition.disposition_name}
                      onMouseDown={(e) => {
                        if (atMax && !isSelected) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      onClick={(e) => {
                        if (atMax && !isSelected) {
                          e.preventDefault();
                          e.stopPropagation();
                          showDispositionLimitError();
                        }
                      }}
                      sx={
                        atMax && !isSelected
                          ? { opacity: 0.55, }
                          : undefined
                      }
                    >
                      <Checkbox checked={isSelected} />
                      <ListItemText primary={disposition.disposition_name} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
          {dispositionSelectError && (
            <Alert severity="error" sx={{ mt: 1}}>
              {dispositionSelectError}
            </Alert>
          )}
        </div>
        {/*end::Header*/}
        {/*begin::Body*/}
        <div className="card-body py-0">
          {allZero ? (
            <div
              style={{
                height: MIN_CHART_HEIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EmptyRows value={emailDashboardDisposition} />
            </div>
          ) : (
            <ResponsiveChartContainer width="100%" height={chartAreaHeight}>
              <PieChartContainer>
                <PieContainer
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="45%"
                  outerRadius="75%"
                  dataKey="value"
                  onClick={(_: any, index: number) => handleSegmentClick(index)}
                  onMouseEnter={(_: any, index: number) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(undefined)}
                  paddingAngle={3}
                  animationBegin={0}
                  animationDuration={600}
                >
                  {chartData.map((d, i) => (
                    <CellContainer
                      key={i}
                      fill={d.fill}
                      strokeWidth={activeIndex === i ? 4 : 2}
                      stroke={activeIndex === i ? "white" : "#ffffff"}
                      style={{
                        filter: activeIndex === i
                          ? `drop-shadow(0 4px 12px ${d.fill}60)`
                          : "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                        transition: "all 0.3s ease",
                        transform: activeIndex === i ? "scale(1.05)" : "scale(1)",
                        transformOrigin: "center",
                      }}
                    />
                  ))}
                </PieContainer>

                <TooltipContainer content={<CustomTooltip />} />
                <LegendContainer
                  content={<CustomLegend />}
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{ paddingLeft: 20 }}
                />
              </PieChartContainer>
            </ResponsiveChartContainer>
          )}
        </div>
        {/*end::Body*/}
      </div>

      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={() => {
          setTableModalOpen(false);
          setSelectedDisposition(null);
          setClickToExpandPage(1);
        }}
        title={
          selectedDisposition
            ? `Disposition – ${selectedDisposition.disposition_name}`
            : "Leads"
        }
        contentLabel="Disposition click-to-expand"
        emptyMessage="No emails in this segment."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx) => row?.thread_id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const threadId = row?.thread_id;
          if (threadId != null) {
            setTableModalOpen(false);
            setSelectedDisposition(null);
            navigate(`/email/inbox/${threadId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={[
          { label: "Subject", render: (row: any) => row?.subject ?? "—" },
          { label: "Status", render: (row: any) => row?.status ?? "—" },
          { label: "Contact Name", render: (row: any) => row?.contact_name ?? "—" },
          { label: "Contact Email", render: (row: any) => row?.contact_email ?? "—" },
          { label: "Contact Phone", render: (row: any) => row?.contact_phone ?? "—" },
          {
            label: "Created",
            render: (row: any) => {
              const raw = row?.created;
              if (!raw) return "—";
              const d = new Date(raw);
              if (Number.isNaN(d.getTime())) return "—";
              const dd = String(d.getDate()).padStart(2, "0");
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh = String(d.getHours()).padStart(2, "0");
              const min = String(d.getMinutes()).padStart(2, "0");
              const ss = String(d.getSeconds()).padStart(2, "0");
              return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
            },
          },
        ]}
      />
    </>
  );
};

export default DispositionCards;